/**
 * Calendar module — per-child events (incl. handover & custody types) plus the
 * custody-schedule view. Two sub-views switched by inner tabs.
 */
import { useMemo, useState } from 'react';
import type { CalendarEvent, CalendarEventType } from '@solandia/core';
import { createCalendarEventInputSchema } from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { useAsync } from '../../hooks/useAsync';
import {
  Badge,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Form,
  SectionHeader,
  Spinner,
} from '../../components/ui';
import { CustodyView } from './CustodyView';

/** Event types offered in the picker, by family mode. The co-parenting types
 *  (handover/custody) are only selectable in separated mode. */
const EVENT_TYPES_SEPARATED: CalendarEventType[] = [
  'appointment',
  'handover',
  'custody',
  'school',
  'activity',
  'other',
];
const EVENT_TYPES_FAMILY: CalendarEventType[] = [
  'appointment',
  'school',
  'activity',
  'other',
];

function typeTone(type: CalendarEventType): 'primary' | 'accent' | 'default' {
  if (type === 'handover' || type === 'custody') return 'accent';
  if (type === 'appointment') return 'primary';
  return 'default';
}

export function CalendarModule() {
  const { t } = useI18n();
  const api = useApi();
  const { family, activeChild } = useFamily();
  const childId = activeChild!.id;
  // In family mode the co-parenting custody view + handover/custody event types
  // are hidden; the underlying data model is unchanged.
  const separated = family.mode === 'separated';
  // Type labels: family mode collapses handover/custody to neutral wording.
  const typeLabels = separated ? t.calendar.typesSeparated : t.calendar.types;
  const [view, setView] = useState<'events' | 'custody'>('events');

  // If the family flipped back to family mode while the custody tab was open,
  // fall back to the events view (the tab is no longer rendered).
  const effectiveView = separated ? view : 'events';

  const { data, loading, error, reload } = useAsync<CalendarEvent[]>(async () => {
    const res = await api.listEvents(family.id, childId);
    return res.items;
  }, [family.id, childId]);

  const sorted = useMemo(
    () => [...(data ?? [])].sort((a, b) => a.startAt.localeCompare(b.startAt)),
    [data],
  );

  const [adding, setAdding] = useState(false);

  async function remove(id: string) {
    if (!window.confirm(t.common.confirmDelete)) return;
    await api.deleteEvent(family.id, childId, id);
    reload();
  }

  return (
    <section>
      <SectionHeader
        title={t.calendar.title}
        action={
          effectiveView === 'events' ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setAdding(true)}
            >
              ＋ {t.calendar.add}
            </button>
          ) : null
        }
      />

      {separated ? (
        <div className="tabs" role="tablist" style={{ marginBottom: 16 }}>
          <button
            type="button"
            role="tab"
            aria-selected={effectiveView === 'events'}
            className={effectiveView === 'events' ? 'tab active' : 'tab'}
            onClick={() => setView('events')}
          >
            {t.calendar.upcoming}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={effectiveView === 'custody'}
            className={effectiveView === 'custody' ? 'tab active' : 'tab'}
            onClick={() => setView('custody')}
          >
            {t.calendar.custodyView}
          </button>
        </div>
      ) : null}

      {effectiveView === 'custody' ? (
        <CustodyView />
      ) : (
        <>
          {loading ? <Spinner /> : null}
          {error ? <ErrorState message={t.errors.generic} onRetry={reload} /> : null}
          {sorted.length === 0 && !loading ? (
            <EmptyState emoji="📅" title={t.calendar.empty} />
          ) : null}
          {sorted.length > 0 ? (
            <ul className="list">
              {sorted.map((ev) => (
                <li key={ev.id} className="list-item">
                  <div className="grow stack-sm">
                    <div className="row wrap">
                      <strong>{ev.title}</strong>
                      <Badge tone={typeTone(ev.type)}>{typeLabels[ev.type]}</Badge>
                    </div>
                    <span className="text-sm muted">{formatRange(ev)}</span>
                    {ev.location ? (
                      <span className="text-sm">📍 {ev.location}</span>
                    ) : null}
                    {ev.note ? <span className="text-sm">{ev.note}</span> : null}
                  </div>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => remove(ev.id)}
                    aria-label={`${t.common.delete} ${ev.title}`}
                  >
                    {t.common.delete}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}

      {adding ? (
        <AddEventDialog
          separated={separated}
          typeLabels={typeLabels}
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
    </section>
  );
}

function formatRange(ev: CalendarEvent): string {
  const start = new Date(ev.startAt);
  if (ev.allDay) {
    return `${start.toLocaleDateString('sk-SK')} · ${'celý deň'}`;
  }
  const end = new Date(ev.endAt);
  const sameDay = start.toDateString() === end.toDateString();
  const opts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  };
  if (sameDay) {
    return `${start.toLocaleString('sk-SK', opts)} – ${end.toLocaleTimeString('sk-SK', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }
  return `${start.toLocaleString('sk-SK', opts)} → ${end.toLocaleString('sk-SK', opts)}`;
}

/** Convert a datetime-local input value to an ISO UTC string. */
function localToIso(local: string): string {
  // `local` is "YYYY-MM-DDTHH:mm" in the user's timezone.
  return new Date(local).toISOString();
}

function nowLocalInput(offsetHours = 0): string {
  const d = new Date(Date.now() + offsetHours * 3_600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function AddEventDialog({
  separated,
  typeLabels,
  onClose,
  onCreated,
}: {
  separated: boolean;
  typeLabels: Record<CalendarEventType, string>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family, activeChild } = useFamily();
  const eventTypes = separated ? EVENT_TYPES_SEPARATED : EVENT_TYPES_FAMILY;
  const [title, setTitle] = useState('');
  const [type, setType] = useState<CalendarEventType>('appointment');
  const [start, setStart] = useState(nowLocalInput(24));
  const [end, setEnd] = useState(nowLocalInput(25));
  const [allDay, setAllDay] = useState(false);
  const [location, setLocation] = useState('');
  const [note, setNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit() {
    const startIso = localToIso(start);
    const endIso = localToIso(allDay ? start : end);
    const input = {
      title: title.trim(),
      type,
      startAt: startIso,
      endAt: endIso,
      allDay,
      location: location.trim() || null,
      note: note.trim() || null,
    };
    const parsed = createCalendarEventInputSchema.safeParse(input);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string') fieldErrors[key] = issue.message;
      }
      if (!fieldErrors.title && !title.trim()) fieldErrors.title = t.common.required;
      setErrors(fieldErrors);
      return;
    }
    setBusy(true);
    try {
      await api.createEvent(family.id, activeChild!.id, parsed.data);
      onCreated();
    } catch {
      setErrors({ title: t.errors.generic });
      setBusy(false);
    }
  }

  return (
    <Dialog title={t.calendar.add} onClose={onClose} closeLabel={t.common.close}>
      <Form onSubmit={submit}>
        <Field label={t.calendar.titleLabel} error={errors.title}>
          {({ id, invalid }) => (
            <input
              id={id}
              className="input"
              value={title}
              aria-invalid={invalid}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          )}
        </Field>
        <Field label={t.calendar.typeLabel}>
          {({ id }) => (
            <select
              id={id}
              className="select"
              value={type}
              onChange={(e) => setType(e.target.value as CalendarEventType)}
            >
              {eventTypes.map((ty) => (
                <option key={ty} value={ty}>
                  {typeLabels[ty]}
                </option>
              ))}
            </select>
          )}
        </Field>
        <label className="scope-toggle" style={{ border: 'none', padding: 0 }}>
          <input
            type="checkbox"
            checked={allDay}
            onChange={(e) => setAllDay(e.target.checked)}
          />
          <span>{t.calendar.allDayLabel}</span>
        </label>
        <div className="form-grid form-grid-2">
          <Field label={t.calendar.startLabel}>
            {({ id }) => (
              <input
                id={id}
                type="datetime-local"
                className="input"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            )}
          </Field>
          {!allDay ? (
            <Field label={t.calendar.endLabel} error={errors.endAt}>
              {({ id, invalid }) => (
                <input
                  id={id}
                  type="datetime-local"
                  className="input"
                  value={end}
                  aria-invalid={invalid}
                  onChange={(e) => setEnd(e.target.value)}
                />
              )}
            </Field>
          ) : null}
        </div>
        <Field label={`${t.calendar.locationLabel} (${t.common.optional})`}>
          {({ id }) => (
            <input
              id={id}
              className="input"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          )}
        </Field>
        <Field label={`${t.common.note} (${t.common.optional})`}>
          {({ id }) => (
            <textarea
              id={id}
              className="textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.common.add}
          </button>
        </div>
      </Form>
    </Dialog>
  );
}
